import { WS_VALIDATE_ABORT_EVENT } from "../../../utils/constants";
import {
  QueueTest,
  QueueTestStatus,
} from "../../command_center/components/center/modals/console/QueuePanel";

export { WS_VALIDATE_ABORT_EVENT };

export const WS_TEST_PLAN_EXECUTION_IN_PROGRESS = "TEST_PLAN_EXECUTION_IN_PROGRESS";
export const WS_TEST_CASE_EXECUTION_IN_PROGRESS = "TEST_CASE_EXECUTION_IN_PROGRESS";
export const WS_TESTPLAN_PROGRESS_UPDATED = "TESTPLAN_PROGRESS_UPDATED";
export const WS_TESTCASE_PROGRESS_UPDATED = "TESTCASE_PROGRESS_UPDATED";
export const WS_TESTCASE_ONBOARD_TIMELINE_UPDATED = "TESTCASE_ONBOARD_TIMELINE_UPDATED";
export const WS_TESTCASE_RESULT_UPDATED = "TESTCASE_RESULT_UPDATED";
export const WS_TESTPLAN_RESULT_UPDATED = "TESTPLAN_RESULT_UPDATED";
export const WS_TEST_PLAN_EXECUTION_COMPLETE = "TEST_PLAN_EXECUTION_COMPLETE";
export const WS_TEST_CASE_EXECUTION_COMPLETE = "TEST_CASE_EXECUTION_COMPLETE";
export const WS_TEST_PLAN_EXECUTION_CANCELLED = "TEST_PLAN_EXECUTION_CANCELLED";
export const WS_TEST_CASE_EXECUTION_CANCELLED = "TEST_CASE_EXECUTION_CANCELLED";
export const WS_TEST_PLAN_EXECUTION_ABORTED = "TEST_PLAN_EXECUTION_ABORTED";
export const WS_TEST_CASE_EXECUTION_ABORTED = "TEST_CASE_EXECUTION_ABORTED";

/**
 * Formats duration in milliseconds into standard telemetry unit strings (sec, min, hr).
 */
export const formatRemainingTimeMS = (ms?: number): string => {
  if (ms === undefined || ms === null || isNaN(ms) || ms <= 0) {
    return "0 min";
  }

  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds} sec`;
  }

  const minutes = Math.round(ms / 60000);
  if (minutes < 120) {
    return `${minutes} min`;
  }

  const hours = (ms / 3600000).toFixed(1);
  return `${hours} hr`;
};

/**
 * Normalizes any raw status string into standard QueueTestStatus.
 */
export const normalizeQueueTestStatus = (
  rawStatus?: string | null,
): QueueTestStatus => {
  const s = String(rawStatus ?? "").trim().toUpperCase();
  if (s.includes("CANCEL") || s.includes("ABORT")) {
    return "cancelled";
  }
  if (
    s === "FAIL" ||
    s === "FAILED" ||
    s === "TIMEOUT" ||
    s === "TIMED_OUT" ||
    s === "TIMEDOUT" ||
    s === "PARTIAL"
  ) {
    return "failed";
  }
  if (
    s === "RUNNING" ||
    s === "IN_PROGRESS" ||
    s === "INPROGRESS" ||
    s === "PENDING" ||
    s === "EXECUTING" ||
    s === "STARTED" ||
    s === "QUEUED" ||
    s === "AWAITING_SCHEDULER_STATUS"
  ) {
    return "running";
  }
  return "passed";
};

/**
 * Formats raw backend status string into a clean, human-readable display label.
 * E.g. "MANUAL_ABORT" -> "Manual Abort", "AWAITING_SCHEDULER_STATUS" -> "Awaiting scheduler status"
 */
export const formatQueueStatusLabel = (rawStatus?: string | null): string => {
  const str = String(rawStatus ?? "").trim();
  if (!str) return "";

  if (str.toUpperCase() === "AWAITING_SCHEDULER_STATUS") {
    return "Awaiting scheduler status";
  }

  // If already user-facing with spaces or mixed case and no underscores, preserve as-is
  if (str.includes(" ") || (/[a-z]/.test(str) && !str.includes("_"))) {
    return str;
  }

  // Convert SCREAMING_SNAKE_CASE or UPPERCASE to Title Case
  return str
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};


/**
 * Accurately extracts passed and failed counts from various payload schemas,
 * computing difference from resolved count when one metric is provided.
 */
export interface PassFailCounts {
  passed: number;
  failed: number;
}

export const extractPassFailCounts = (
  raw: any,
  fallbackPassed?: number,
  fallbackFailed?: number,
): PassFailCounts => {
  const item = raw as any;
  const resolved =
    item?.resolvedTestCases ?? item?.completed ?? item?.completedEntries;

  let passed =
    item?.passed ??
    item?.Passed ??
    item?.pass ??
    item?.Pass ??
    item?.passedCount ??
    item?.passCount ??
    item?.PassCount;

  let failed =
    item?.failed ??
    item?.Failed ??
    item?.fail ??
    item?.Fail ??
    item?.failedCount ??
    item?.failCount ??
    item?.FailCount;

  if (passed === undefined && resolved !== undefined && failed !== undefined) {
    passed = Math.max(0, Number(resolved) - Number(failed));
  } else if (passed === undefined) {
    passed = fallbackPassed ?? 0;
  }

  if (failed === undefined && resolved !== undefined && passed !== undefined) {
    failed = Math.max(0, Number(resolved) - Number(passed));
  } else if (failed === undefined) {
    failed = fallbackFailed ?? 0;
  }

  return {
    passed: Number(passed),
    failed: Number(failed),
  };
};

/**
 * Computes remaining time and total estimated duration string.
 */
export const calculateProgressDurations = (
  body: any,
  prevDuration?: string,
  total?: number,
  completed?: number,
): { duration: string; remainingDuration?: string } => {
  let totalDurationStr: string = prevDuration || "0 min";
  let remainingDuration: string | undefined;

  if (
    typeof body?.estimatedDurationMS === "number" &&
    body.estimatedDurationMS > 0
  ) {
    totalDurationStr = formatRemainingTimeMS(body.estimatedDurationMS);
  }

  if (typeof body?.remainingTimeMS === "number" && body.remainingTimeMS >= 0) {
    const remStr = formatRemainingTimeMS(body.remainingTimeMS);
    if (!totalDurationStr || totalDurationStr === "0 min") {
      const remainingPct = Number(
        body?.remainingPercent ??
          (total && total > 0 && completed !== undefined
            ? ((total - completed) / total) * 100
            : 0),
      );
      if (remainingPct > 0 && remainingPct < 100) {
        const totalMs = (body.remainingTimeMS / remainingPct) * 100;
        totalDurationStr = formatRemainingTimeMS(totalMs);
      }
    }

    if (totalDurationStr && totalDurationStr !== "0 min") {
      remainingDuration = `${remStr} left (${totalDurationStr})`;
    } else {
      remainingDuration = `${remStr} left`;
    }
  } else if (
    typeof body?.estimatedDurationMS === "number" &&
    body.estimatedDurationMS > 0 &&
    (!totalDurationStr || totalDurationStr === "0 min")
  ) {
    totalDurationStr = formatRemainingTimeMS(body.estimatedDurationMS);
  }

  return { duration: totalDurationStr, remainingDuration };
};

/**
 * Filters executed tests to exclude tests that are still running / in-progress.
 * Executed tests remain visible independently even when the same test is re-run in active section.
 */
export const filterVisibleExecutedTests = (
  executedTests: QueueTest[],
  _activeTest?: QueueTest | null,
  _activeExecutionInfo?: any,
): QueueTest[] => {
  if (!executedTests || executedTests.length === 0) return [];

  return executedTests.filter((test) => test.status !== "running");
};

/**
 * Checks whether a WebSocket message should trigger a validation
 * console / report reload.
 */
export const isValidateReloadEvent = (msg: any): boolean => {
  const type = msg?.message || msg?.eventType || msg?.type;
  return (
    msg?.count > 0 ||
    type === WS_VALIDATE_ABORT_EVENT ||
    type === "TEST_CASE_EXECUTION_ABORTED" ||
    type === "TEST_PLAN_EXECUTION_ABORTED" ||
    type === "TEST_CASE_EXECUTION_SKIPPED" ||
    type === "TEST_CASE_EXECUTION_PARTIALLY_COMPLETED" ||
    (typeof type === "string" && type.includes("RESULT_UPDATED"))
  );
};
